import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FacilityEvent, FacilityEventResult } from "@app/shared";
import { debounce, toastWarning } from "../helpers";

/**
 * Cursor-paginated infinite-scroll state for the events grids (org- and facility-level).
 *
 * `fetchPage` must be memoized by the caller (e.g. `useCallback` keyed on the current query/
 * filters); whenever its identity changes the list resets and re-fetches from the top. Pass
 * `enabled={false}` to suspend fetching (e.g. device/live mode that renders local events instead).
 */
export function useInfiniteEvents(fetchPage: (cursor?: string) => Promise<FacilityEventResult | undefined>, enabled = true) {
    const [events, setEvents] = useState<FacilityEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState<string>();
    const nextCursorRef = useRef<string | undefined>(undefined);
    // Synchronous in-flight lock. The `isLoading` state guard is async (only visible after a
    // re-render), so a bouncing scroll wheel can fire several load-more calls before it flips,
    // each with the same stale cursor. This ref blocks re-entry immediately.
    const isFetchingRef = useRef(false);

    const load = useCallback(
        async (reset: boolean, cursor?: string) => {
            isFetchingRef.current = true;
            setIsLoading(true);
            try {
                const response = await fetchPage(cursor);
                setEvents((prev) => {
                    if (reset) return response?.data ?? [];
                    const seen = new Set(prev.map((e) => e.id));
                    return [...prev, ...(response?.data ?? []).filter((e) => !seen.has(e.id))];
                });
                setHasMore(response?.hasMore ?? false);
                nextCursorRef.current = response?.nextCursor;
                setError(undefined);
            } catch (e) {
                console.error("Failed to fetch events:", e);
                toastWarning("Failed to load events. Please try again later.");
                setError("Failed to load events. Please try again later.");
            } finally {
                setIsLoading(false);
                isFetchingRef.current = false;
            }
        },
        [fetchPage]
    );

    const debouncedReset = useMemo(() => debounce(() => load(true), 300), [load]);

    useEffect(() => {
        setEvents([]);
        setHasMore(true);
        nextCursorRef.current = undefined;
        if (enabled) debouncedReset();
    }, [enabled, debouncedReset]);

    const loadMore = useCallback(async () => {
        if (isFetchingRef.current || isLoading || !hasMore || !nextCursorRef.current) return;
        await load(false, nextCursorRef.current);
    }, [hasMore, isLoading, load]);

    return { events, setEvents, isLoading, hasMore, error, loadMore };
}
